import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useAppConfig, useBrandAppConfig } from "../api/cache/appConfigQueryOptions.js";
import { getBrandLogo } from "../utils/assetUtils.js";
import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { ConciergeChatAreaV2 } from "./components/ConciergeChatAreaV2/index.js";
import { ConciergeHeader } from "./components/ConciergeHeader/index.js";
import { ConciergeSidebar } from "./components/ConciergeSidebar.js";
import { useAutoScroll } from "./hooks/useAutoScroll.js";
import { useAutoSendFromUrl } from "./hooks/useAutoSendFromUrl.js";
import { useConversationLoader } from "./hooks/useConversationLoader.js";
import { useConciergeContext } from "./ConciergeContextProvider.js";
import DevOptionsPanel from "./components/DevOptionsPanel.js";
import { useDevOptions } from "./hooks/useDevOptions.js";
import { useConciergeChat } from "./hooks/useConciergeChat.js";
import { useUrlSync } from "./hooks/useUrlSync.js";
import { Drawer } from "@mui/material";
import { BRANDCLUB_BRAND_ID, BRANDCLUB_BRAND_NAME } from "../utils/constants/Constants.js";
const SIDEBAR_EXPANDED_STORAGE_KEY = "concierge:sidebar:expanded";
const getInitialSidebarExpanded = () => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    const rawValue = window.localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY);
    if (rawValue === null) {
      return false;
    }
    return rawValue === "true";
  } catch {
    return false;
  }
};
const ConciergeContainer = ({
  layout = "header",
  isEmbedded,
  cartCount = 0,
  onBagClick,
  isCheckoutDrawerOpen = false,
  CheckoutDrawerContent,
  onVariantSelect,
  userProfile,
  onSignOutClick,
  onHomeClick,
  userFirstName
}) => {
  const conversationState = useConciergeContext();
  const [appLogoUrl, setAppLogoUrl] = useState(void 0);
  const [appName, setAppName] = useState(void 0);
  const [brandId, setBrandId] = useState(void 0);
  const params = new URLSearchParams(window.location.search);
  const appConfigId = params.get("appConfigId") || null;
  const { data: appConfig } = useAppConfig();
  const { data: brandAppConfig } = useBrandAppConfig();
  useEffect(() => {
    var _a, _b;
    if (appConfig) {
      const brandId2 = ((_a = appConfig == null ? void 0 : appConfig.domainConfig) == null ? void 0 : _a.brandId) || BRANDCLUB_BRAND_ID;
      const appLogoUrl2 = getBrandLogo(brandId2);
      const appName2 = ((_b = appConfig == null ? void 0 : appConfig.domainConfig) == null ? void 0 : _b.brandName) || BRANDCLUB_BRAND_NAME;
      setAppLogoUrl(appLogoUrl2);
      setAppName(appName2);
      setBrandId(brandId2);
    }
  }, [appConfig]);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(getInitialSidebarExpanded);
  const [searchInputValue, setSearchInputValue] = useState("");
  const devOpts = useDevOptions();
  const handleStreamError = (error, originalMessage) => {
    var _a;
    setSearchInputValue(originalMessage);
    const msg = ((_a = error == null ? void 0 : error.message) == null ? void 0 : _a.toLowerCase()) ?? "";
    let errorType = "unknown";
    if (msg.includes("429") || msg.includes("rate limit")) errorType = "rate_limit";
    else if (msg.includes("400") || msg.includes("bad request")) errorType = "bad_request";
    else if (msg.includes("network") || msg.includes("failed to fetch")) errorType = "network";
    else if (msg.includes("500") || msg.includes("server")) errorType = "server";
    const errorMessages = {
      rate_limit: "Too many requests. Please wait a moment before trying again.",
      bad_request: "Please enter a message before sending.",
      network: "Network error. Please check your connection and try again.",
      server: "Something went wrong on our end. Please try again.",
      unknown: "Something went wrong. Please try again."
    };
    toast.error(errorMessages[errorType]);
  };
  const chat = useConciergeChat({
    apiBaseUrl: conversationState.apiBaseUrl || "/api-rewards",
    conversationId: conversationState.conversationId,
    sharedConversationId: conversationState.sharedConversationId,
    brandId: (brandId == null ? void 0 : brandId.toString()) ?? null,
    appConfigId,
    devOptions: devOpts.devOptions,
    onError: (error) => handleStreamError(error, searchInputValue),
    onTripwire: (message) => {
      toast.error(message);
    },
    onConversationIdGenerated: (id) => {
      conversationState.setIsActivelyStreaming(true);
      conversationState.setConversationId(id);
      conversationState.setSharedConversationId(null);
      conversationState.setIsSharedView(false);
      conversationState.setSharedAtMessageIndex(void 0);
    }
  });
  const effectiveMessages = chat.messages.length > 0 ? chat.messages : conversationState.conversationHistory;
  const {
    shouldScrollOnNextMessageRef
  } = useAutoScroll(
    effectiveMessages.length,
    conversationState.isActivelyStreaming,
    conversationState.streamingAIResponse,
    effectiveMessages.length === 0
  );
  useEffect(() => {
    const active = chat.isStreaming || chat.isLoading;
    conversationState.setIsActivelyStreaming(active);
    if (chat.isStreaming) {
      const lastMsg = chat.messages[chat.messages.length - 1];
      const hasActiveParts = (lastMsg == null ? void 0 : lastMsg.role) === "assistant" && Array.isArray(lastMsg.parts) && lastMsg.parts.some((p) => {
        var _a;
        return p.type !== "text" || ((_a = p.text) == null ? void 0 : _a.length) > 0;
      });
      if (!hasActiveParts) {
        const text = (lastMsg == null ? void 0 : lastMsg.role) === "assistant" ? lastMsg.parts.filter((p) => p.type === "text" && "text" in p).map((p) => p.text).join("") : "";
        conversationState.setStreamingAIResponse(text);
      }
    }
    if (!active) {
      conversationState.clearStreamingState();
    }
  }, [chat.isStreaming, chat.isLoading, chat.messages]);
  const performSearch = useCallback(
    async (text, files) => {
      shouldScrollOnNextMessageRef.current = true;
      await chat.sendMessage(text, files);
    },
    [chat, shouldScrollOnNextMessageRef]
  );
  const cancelStream = useCallback(() => {
    chat.stop();
  }, [chat]);
  const resetChatMessages = useCallback(() => {
    var _a;
    (_a = chat.setMessages) == null ? void 0 : _a.call(chat, []);
  }, [chat]);
  const handleSidebarToggle = useCallback(() => {
    setIsSidebarExpanded((prev) => {
      const nextValue = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, String(nextValue));
      } catch {
      }
      return nextValue;
    });
  }, []);
  const queryClient = useQueryClient();
  const refreshConversationHistory = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }, 5e3);
  }, [queryClient]);
  const handleSearch = async (searchTerm, files) => {
    setSearchInputValue("");
    refreshConversationHistory();
    await performSearch(searchTerm, files);
  };
  const handleUpdateFeedback = (messageIndex, feedback) => {
    conversationState.setConversationHistory((prev) => {
      return prev.map((msg, idx) => {
        if (idx === messageIndex) {
          return {
            ...msg,
            feedback: feedback || void 0
          };
        }
        return msg;
      });
    });
  };
  const handleConversationSelect = (conversationId) => {
    cancelStream();
    resetChatMessages();
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    if (conversationState.conversationId === conversationId) {
      conversationState.setConversationId(null);
      setTimeout(() => {
        conversationState.setConversationId(conversationId);
      }, 0);
      return;
    }
    conversationState.setConversationHistory([]);
    conversationState.setStreamingAIResponse("");
    conversationState.setFollowUpQuestions([]);
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(void 0);
    conversationState.setSharedAtMessageIndex(void 0);
    conversationState.setSourceConversationId(void 0);
    conversationState.setSourceCheckpointIndex(void 0);
    conversationState.setSharedConversationId(null);
    const params2 = new URLSearchParams(window.location.search);
    params2.set("conversationId", conversationId);
    params2.delete("sharedConversationId");
    const newUrl = `${window.location.pathname}?${params2.toString()}`;
    window.history.pushState({}, "", newUrl);
    conversationState.setConversationId(conversationId);
    conversationState.setSharedConversationId(null);
  };
  const handleComposeClick = () => {
    cancelStream();
    resetChatMessages();
    conversationState.setIsActivelyStreaming(false);
    conversationState.clearStreamingState();
    conversationState.setConversationHistory([]);
    conversationState.setStreamingAIResponse("");
    conversationState.setFollowUpQuestions([]);
    conversationState.setConversationId(null);
    conversationState.setIsSharedView(false);
    conversationState.setSharedByUserName(void 0);
    conversationState.setSharedAtMessageIndex(void 0);
    conversationState.setSourceConversationId(void 0);
    conversationState.setSourceCheckpointIndex(void 0);
    conversationState.setSharedConversationId(null);
    const params2 = new URLSearchParams(window.location.search);
    params2.delete("conversationId");
    params2.delete("sharedConversationId");
    const newUrl = params2.toString() ? `${window.location.pathname}?${params2.toString()}` : window.location.pathname;
    window.history.pushState({}, "", newUrl);
  };
  const { isLoadingConversationQuery, isPendingAssistantResponse, isPollingTimedOut, resetPendingState } = useConversationLoader({
    conversationId: conversationState.conversationId,
    sharedConversationId: conversationState.sharedConversationId,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationHistoryLength: effectiveMessages.length,
    apiBaseUrl: conversationState.apiBaseUrl,
    onConversationLoaded: (messages, followUpQuestions, sharingInfo) => {
      var _a;
      conversationState.setConversationHistory(messages);
      conversationState.setFollowUpQuestions(followUpQuestions);
      (_a = chat.setMessages) == null ? void 0 : _a.call(chat, messages);
      if (sharingInfo) {
        conversationState.setIsSharedView(sharingInfo.isSharedView ?? false);
        conversationState.setSharedConversationId(sharingInfo.sharedConversationId || null);
        conversationState.setSharedByUserName(sharingInfo.sharedByUserName);
        conversationState.setSharedAtMessageIndex(sharingInfo.sharedAtMessageIndex);
        conversationState.setSourceConversationId(sharingInfo.sourceConversationId);
        conversationState.setSourceCheckpointIndex(sharingInfo.sourceCheckpointIndex);
      } else {
        conversationState.setSharedConversationId(null);
        conversationState.setIsSharedView(false);
        conversationState.setSharedByUserName(void 0);
        conversationState.setSharedAtMessageIndex(void 0);
        conversationState.setSourceConversationId(void 0);
        conversationState.setSourceCheckpointIndex(void 0);
      }
    },
    onConversationNotFound: () => {
      toast.error("Conversation not found.");
      const params2 = new URLSearchParams(window.location.search);
      params2.delete("conversationId");
      params2.delete("sharedConversationId");
      const newUrl = params2.toString() ? `${window.location.pathname}?${params2.toString()}` : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
      conversationState.setConversationId(null);
      conversationState.setSharedConversationId(null);
    }
  });
  const handleRetry = () => {
    resetPendingState();
    const history = conversationState.conversationHistory;
    if (history.length === 0) return;
    const lastMsg = history[history.length - 1];
    if (lastMsg.role !== "user") return;
    const lastUserText = (lastMsg.parts || []).filter((p) => p.type === "text" && typeof p.text === "string").map((p) => p.text).join("").trim();
    if (!lastUserText) return;
    conversationState.setConversationHistory(history.slice(0, -1));
    handleSearch(lastUserText);
  };
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !conversationState.isActivelyStreaming) {
      refreshConversationHistory();
    }
    wasStreamingRef.current = conversationState.isActivelyStreaming;
  }, [conversationState.isActivelyStreaming, refreshConversationHistory]);
  const wasPendingRef = useRef(false);
  useEffect(() => {
    if (wasPendingRef.current && !isPendingAssistantResponse) {
      refreshConversationHistory();
    }
    wasPendingRef.current = isPendingAssistantResponse;
  }, [isPendingAssistantResponse, refreshConversationHistory]);
  useUrlSync(conversationState.conversationId, conversationState.sharedConversationId);
  const wrappedOnSignIn = useCallback(() => {
    if (!conversationState.onSignIn) return;
    const msgs = chat.messages.length > 0 ? chat.messages : conversationState.conversationHistory;
    const lastUserMsg = [...msgs].reverse().find((m) => m.role === "user");
    const content = lastUserMsg ? "parts" in lastUserMsg ? lastUserMsg.parts.filter((p) => p.type === "text").map((p) => p.text).join("") : lastUserMsg.content : void 0;
    conversationState.onSignIn(content);
  }, [conversationState, chat.messages]);
  const hasCheckedPendingLoginRef = useRef(false);
  useEffect(() => {
    if (hasCheckedPendingLoginRef.current) return;
    const pendingMessage = sessionStorage.getItem("concierge_resend");
    if (!pendingMessage) {
      hasCheckedPendingLoginRef.current = true;
      return;
    }
    if (!conversationState.isSignedIn) return;
    hasCheckedPendingLoginRef.current = true;
    sessionStorage.removeItem("concierge_resend");
    conversationState.setConversationHistory([]);
    conversationState.setConversationId(null);
    conversationState.setSharedConversationId(null);
    conversationState.clearStreamingState();
    setTimeout(() => handleSearch(pendingMessage), 500);
  }, [handleSearch, conversationState]);
  useAutoSendFromUrl({
    isActivelyStreaming: conversationState.isActivelyStreaming,
    conversationLength: effectiveMessages.length,
    onSearch: handleSearch
  });
  const showWelcome = effectiveMessages.length === 0 && !isLoadingConversationQuery && !isPendingAssistantResponse;
  const chatAreaProps = {
    appName,
    conciergeName: brandAppConfig == null ? void 0 : brandAppConfig.conciergeAgentName,
    brandId: (brandId == null ? void 0 : brandId.toString()) ?? null,
    conversationId: conversationState.conversationId,
    apiBaseUrl: conversationState.apiBaseUrl,
    isEmbedded,
    userFirstName,
    conversationHistory: effectiveMessages,
    followUpQuestions: conversationState.followUpQuestions,
    streamingStoreLocator: conversationState.streamingStoreLocator,
    streamingLoginRequired: conversationState.streamingLoginRequired,
    streamingDevInfo: conversationState.streamingDevInfo,
    onSignIn: wrappedOnSignIn,
    isActivelyStreaming: conversationState.isActivelyStreaming,
    transientStatus: chat.transientStatus,
    streamingAIResponse: conversationState.streamingAIResponse,
    isLoadingConversationQuery,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry: handleRetry,
    onRetryLastMessage: chat.retryLastMessage,
    handleSearch,
    onUpdateFeedback: handleUpdateFeedback,
    onVariantSelect,
    onStop: cancelStream,
    searchInputValue,
    setSearchInputValue,
    brandLogoUrl: appLogoUrl,
    ...layout === "sidebar" && {
      onHomeClick,
      isSidebarExpanded
    },
    // Mobile sidebar overlay props - enables the hamburger menu on mobile
    mobileSidebarProps: {
      onConversationSelect: handleConversationSelect,
      onComposeClick: handleComposeClick,
      currentConversationId: conversationState.conversationId,
      userProfile,
      apiBaseUrl: conversationState.apiBaseUrl,
      onSignOutClick,
      onHomeClick,
      brandLogoUrl: appLogoUrl
    },
    // Sharing state
    isSharedView: conversationState.isSharedView,
    sharedByUserName: conversationState.sharedByUserName,
    sourceConversationId: conversationState.sourceConversationId,
    sourceCheckpointIndex: conversationState.sourceCheckpointIndex
  };
  const devOptionsPanel = /* @__PURE__ */ jsx(DevOptionsPanel, { ...devOpts });
  const checkoutDrawer = CheckoutDrawerContent && /* @__PURE__ */ jsx(
    Drawer,
    {
      id: "cart-popover",
      open: isCheckoutDrawerOpen,
      sx: {
        zIndex: 1300,
        width: 462,
        maxWidth: "100%"
      },
      PaperProps: {
        sx: {
          boxShadow: "none",
          borderRadius: "0",
          maxWidth: "100%"
        }
      },
      anchor: "right",
      disableAutoFocus: true,
      disableEnforceFocus: true,
      disableRestoreFocus: true,
      children: /* @__PURE__ */ jsx(CheckoutDrawerContent, {})
    }
  );
  if (layout === "sidebar") {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Toaster, {}),
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: "concierge-root",
          style: {
            display: "flex",
            flexDirection: "row",
            height: "100dvh",
            minHeight: "-webkit-fill-available"
          },
          children: [
            /* @__PURE__ */ jsx(
              ConciergeSidebar,
              {
                onConversationSelect: handleConversationSelect,
                onComposeClick: handleComposeClick,
                currentConversationId: conversationState.conversationId,
                userProfile,
                apiBaseUrl: conversationState.apiBaseUrl,
                onSignOutClick,
                onHomeClick,
                brandLogoUrl: appLogoUrl,
                isExpanded: isSidebarExpanded,
                onToggle: handleSidebarToggle
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "tw:relative tw:flex tw:flex-1 tw:flex-col", style: { minWidth: 0 }, children: [
              devOptionsPanel,
              /* @__PURE__ */ jsx(ConciergeChatAreaV2, { ...chatAreaProps })
            ] })
          ]
        }
      ),
      checkoutDrawer
    ] });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Toaster, {}),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-root",
        style: {
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          minHeight: "-webkit-fill-available"
        },
        children: [
          /* @__PURE__ */ jsx(
            ConciergeHeader,
            {
              onConversationSelect: handleConversationSelect,
              onComposeClick: handleComposeClick,
              showComposeIcon: !showWelcome,
              cartCount,
              onBagClick
            }
          ),
          devOptionsPanel,
          /* @__PURE__ */ jsx(ConciergeChatAreaV2, { ...chatAreaProps })
        ]
      }
    ),
    checkoutDrawer
  ] });
};
export {
  ConciergeContainer as default
};
//# sourceMappingURL=ConciergeContainer.js.map
